import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  AdminPanelSettings as PermissionsIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { getFirestore, doc, getDoc, setDoc } from '../../services/firestoreCompat';

// Role definitions - matching Liefersoft
const ROLES = [
  { key: 'owner', translationKey: 'settings.role_owner', fallback: 'Owner' },
  { key: 'restaurant_admin', translationKey: 'settings.role_restaurant_admin', fallback: 'Restaurant admin' },
  { key: 'admin', translationKey: 'settings.role_admin', fallback: 'Admin' },
  { key: 'cashier', translationKey: 'settings.role_cashier', fallback: 'Cashier' },
  { key: 'waiter', translationKey: 'settings.role_waiter', fallback: 'Waiter' },
  { key: 'cook', translationKey: 'settings.role_cook', fallback: 'Cook' },
  { key: 'driver', translationKey: 'settings.role_driver', fallback: 'Driver' },
];

// Pages/sections that can be access-controlled
const PAGES = [
  { key: 'home', translationKey: 'settings.page_home', fallback: 'Home' },
  { key: 'all_orders', translationKey: 'settings.page_all_orders', fallback: 'All orders' },
  { key: 'delivery_service', translationKey: 'settings.page_delivery', fallback: 'Delivery service' },
  { key: 'cash_desk', translationKey: 'settings.page_cash_desk', fallback: 'Cash desk' },
  { key: 'restaurant', translationKey: 'settings.page_restaurant', fallback: 'Restaurant' },
  { key: 'kitchen_screen', translationKey: 'settings.page_kitchen', fallback: 'Kitchen screen' },
  { key: 'driver_screen', translationKey: 'settings.page_driver', fallback: 'Driver screen' },
  { key: 'reports', translationKey: 'settings.page_reports', fallback: 'Reports' },
  { key: 'backoffice', translationKey: 'settings.page_backoffice', fallback: 'Backoffice' },
  { key: 'settings', translationKey: 'settings.page_settings', fallback: 'Settings' },
];

type AccessLevel = 'access' | 'not_visible';

interface PermissionsMatrix {
  [pageKey: string]: {
    [roleKey: string]: AccessLevel;
  };
}

// Default permissions: owner/restaurant_admin/admin get access to everything
const getDefaultPermissions = (): PermissionsMatrix => {
  const matrix: PermissionsMatrix = {};
  PAGES.forEach((page) => {
    matrix[page.key] = {};
    ROLES.forEach((role) => {
      if (['owner', 'restaurant_admin', 'admin'].includes(role.key)) {
        matrix[page.key][role.key] = 'access';
      } else if (role.key === 'cashier') {
        matrix[page.key][role.key] = ['home', 'all_orders', 'cash_desk', 'restaurant'].includes(page.key) ? 'access' : 'not_visible';
      } else if (role.key === 'waiter') {
        matrix[page.key][role.key] = ['home', 'all_orders', 'restaurant'].includes(page.key) ? 'access' : 'not_visible';
      } else if (role.key === 'cook') {
        matrix[page.key][role.key] = ['home', 'kitchen_screen'].includes(page.key) ? 'access' : 'not_visible';
      } else if (role.key === 'driver') {
        matrix[page.key][role.key] = ['home', 'driver_screen'].includes(page.key) ? 'access' : 'not_visible';
      } else {
        matrix[page.key][role.key] = 'not_visible';
      }
    });
  });
  return matrix;
};

const PermissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [permissions, setPermissions] = useState<PermissionsMatrix>(getDefaultPermissions());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadPermissions = useCallback(async () => {
    try {
      const db = getFirestore();
      const docRef = doc(db, 'settings', 'permissions');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.matrix) {
          setPermissions(data.matrix);
        }
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const handlePermissionChange = (pageKey: string, roleKey: string, value: AccessLevel) => {
    setPermissions((prev) => ({
      ...prev,
      [pageKey]: {
        ...prev[pageKey],
        [roleKey]: value,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const db = getFirestore();
      const docRef = doc(db, 'settings', 'permissions');
      await setDoc(docRef, {
        matrix: permissions,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      setSnackbar({ open: true, message: t('settings.permissions_saved') || 'Permissions saved successfully', severity: 'success' });
    } catch (error) {
      console.error('Error saving permissions:', error);
      setSnackbar({ open: true, message: t('settings.permissions_save_error') || 'Error saving permissions', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#00C896' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, pb: { xs: 4, md: 3 }, maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/settings/program/users')}
            sx={{
              color: '#e0e0e0 !important',
              backgroundColor: '#2a2d42 !important',
              borderRadius: '8px',
              px: 2.5,
              py: 1,
              textTransform: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#33364d !important' },
            }}
          >
            {t('settings.back') || 'Back'}
          </Button>
          <PermissionsIcon sx={{ fontSize: 28, color: '#9ca3af' }} />
          <Typography
            sx={{
              fontSize: '1.3rem',
              fontWeight: 600,
              color: '#ffffff !important',
            }}
          >
            {t('settings.access_settings') || 'Access settings'}
          </Typography>
        </Box>
        <Button
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            backgroundColor: '#00C896 !important',
            color: '#1a1d2e !important',
            borderRadius: '8px',
            px: 3,
            py: 1,
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#7bc912 !important' },
            '&:disabled': { opacity: 0.6 },
          }}
        >
          {saving ? (t('settings.saving') || 'Saving...') : (t('settings.save') || 'Save')}
        </Button>
      </Box>

      {/* Permissions Matrix Table */}
      <TableContainer
        sx={{
          backgroundColor: '#1e2139 !important',
          borderRadius: '12px',
          border: '1px solid #3a3d52',
          overflow: 'auto',
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: '#1a1d2e !important',
                  color: '#e0e0e0 !important',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderBottom: '2px solid #3a3d52 !important',
                  minWidth: 160,
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                }}
              >
                {t('settings.page_section') || 'Page / Section'}
              </TableCell>
              {ROLES.map((role) => (
                <TableCell
                  key={role.key}
                  align="center"
                  sx={{
                    backgroundColor: '#1a1d2e !important',
                    color: '#e0e0e0 !important',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderBottom: '2px solid #3a3d52 !important',
                    minWidth: 130,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t(role.translationKey) || role.fallback}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {PAGES.map((page, idx) => (
              <TableRow
                key={page.key}
                sx={{
                  backgroundColor: idx % 2 === 0 ? '#1e2139 !important' : '#222540 !important',
                  '&:hover': { backgroundColor: '#2a2d4a !important' },
                }}
              >
                <TableCell
                  sx={{
                    color: '#e0e0e0 !important',
                    fontWeight: 500,
                    fontSize: '0.85rem',
                    borderBottom: '1px solid #2a2d3e !important',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: idx % 2 === 0 ? '#1e2139 !important' : '#222540 !important',
                    zIndex: 1,
                  }}
                >
                  {t(page.translationKey) || page.fallback}
                </TableCell>
                {ROLES.map((role) => {
                  const value = permissions[page.key]?.[role.key] || 'not_visible';
                  return (
                    <TableCell
                      key={role.key}
                      align="center"
                      sx={{
                        borderBottom: '1px solid #2a2d3e !important',
                        py: 0.8,
                      }}
                    >
                      <Select
                        value={value}
                        onChange={(e) => handlePermissionChange(page.key, role.key, e.target.value as AccessLevel)}
                        size="small"
                        sx={{
                          minWidth: 110,
                          height: 32,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          borderRadius: '6px !important',
                          backgroundColor: value === 'access'
                            ? 'rgba(0, 200, 150, 0.15) !important'
                            : '#2d3048 !important',
                          color: value === 'access'
                            ? '#00C896 !important'
                            : '#9ca3af !important',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: value === 'access'
                              ? 'rgba(0, 200, 150, 0.3) !important'
                              : '#3a3d52 !important',
                          },
                          '& .MuiSelect-icon': {
                            color: '#6b7280 !important',
                            fontSize: 18,
                          },
                          '& fieldset': {
                            borderRadius: '6px !important',
                          },
                        }}
                      >
                        <MenuItem value="access" sx={{ fontSize: '0.8rem' }}>
                          {t('settings.access') || 'Access'}
                        </MenuItem>
                        <MenuItem value="not_visible" sx={{ fontSize: '0.8rem' }}>
                          {t('settings.not_visible') || 'Not visible'}
                        </MenuItem>
                      </Select>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PermissionsPage;
